import {
  DeviceState,
  GameMode,
  POWER_THRESHOLDS,
  SYNC_GRADE_THRESHOLDS,
  SYNC_GRADE_TOTAL,
  WEAPON_MAX_LEVEL,
} from '../constants';
import { nextGateTicks } from '../stage/gateCue';
import { FORMATION_NAMES } from '../formation/formations';
import type { InputManager } from '../input/InputManager';
import { CHAIN_WINDOW_TICKS, type GameWorld } from '../world/GameWorld';
import { CORE_BEAMS, DEVICES, NODE_LENSES } from '../weapons/definitions';
import type { StageRankingResult } from './StageRankingStore';

export interface RuntimeUISnapshot {
  readonly revision: number;
  readonly mode: GameMode;
  readonly difficultyName: string;
  readonly stageId: number;
  readonly stageName: string;
  readonly stageSubtitle: string;
  readonly stageProgress: number;
  readonly score: number;
  readonly lux: number;
  readonly continuousArsenal: boolean;
  readonly syncTotal: number;
  readonly syncTarget: number;
  readonly lives: number;
  readonly maxLives: number;
  readonly chain: number;
  readonly chainTimerFraction: number;
  readonly weaponLevel: number;
  readonly powerCharge: number;
  readonly powerNeeded: number;
  readonly formation: string;
  readonly nodeDirection: number;
  readonly nodeCharge0: number;
  readonly nodeCharge1: number;
  readonly nodeCharge2: number;
  readonly coreName: string;
  readonly coreGlyph: string;
  readonly lensName: string;
  readonly lensGlyph: string;
  readonly deviceName: string;
  readonly deviceGlyph: string;
  readonly deviceState: DeviceState;
  readonly deviceCooldown: number;
  readonly bossActive: boolean;
  readonly bossName: string;
  readonly bossHp: number;
  readonly bossPhase: string;
  readonly musicLayer: number;
  readonly stageBonus: number;
  readonly stageRanking: StageRankingResult | null;
  readonly gateSelection: number;
  readonly gateMessage: string;
  /** Ticks until the next Gate cue on this stage, -1 when none is coming. */
  readonly gateAheadTicks: number;
  readonly gateApproachTicks: number;
  readonly continuesUsed: number;
  readonly lastInputKind: InputManager['lastInputKind'];
  readonly droppedFrames: number;
  readonly fps: number;
  readonly renderer: string;
  readonly audioState: string;
  readonly error: string;
}

export interface RuntimeUIStatusSnapshot {
  readonly renderer: string;
  readonly audioState: string;
  readonly fps: number;
  readonly lastInputKind: InputManager['lastInputKind'];
  readonly droppedFrames: number;
}

const INITIAL: RuntimeUISnapshot = Object.freeze({
  revision: 0,
  mode: GameMode.Boot,
  difficultyName: 'BRIGHT',
  stageId: 0,
  stageName: 'GLASS CURRENT',
  stageSubtitle: 'REFRACTED RECORD RIVER',
  stageProgress: 0,
  score: 0,
  lux: 0,
  continuousArsenal: false,
  syncTotal: 0,
  syncTarget: SYNC_GRADE_TOTAL,
  lives: 3,
  maxLives: 3,
  chain: 0,
  chainTimerFraction: 0,
  weaponLevel: 0,
  powerCharge: 0,
  powerNeeded: POWER_THRESHOLDS[0],
  formation: 'FRONT',
  nodeDirection: 0,
  nodeCharge0: 1,
  nodeCharge1: 1,
  nodeCharge2: 1,
  coreName: 'PULSE',
  coreGlyph: '▸',
  lensName: 'FOCUS',
  lensGlyph: '→',
  deviceName: 'REPAIR FIELD',
  deviceGlyph: '+',
  deviceState: DeviceState.Ready,
  deviceCooldown: 0,
  bossActive: false,
  bossName: '',
  bossHp: 0,
  bossPhase: '',
  musicLayer: 0,
  stageBonus: 0,
  stageRanking: null,
  gateSelection: 0,
  gateMessage: '',
  gateAheadTicks: -1,
  gateApproachTicks: 300,
  continuesUsed: 0,
  lastInputKind: 'keyboard',
  droppedFrames: 0,
  fps: 0,
  renderer: 'initializing',
  audioState: 'suspended',
  error: '',
});

function syncTotalForGrade(weaponLevel: number, powerCharge: number): number {
  if (weaponLevel >= WEAPON_MAX_LEVEL) return SYNC_GRADE_TOTAL;
  let total = Math.max(0, powerCharge | 0);
  for (let level = 0; level < weaponLevel; level += 1) {
    total += SYNC_GRADE_THRESHOLDS[level] ?? 0;
  }
  return Math.min(SYNC_GRADE_TOTAL, total);
}

const INITIAL_STATUS: RuntimeUIStatusSnapshot = Object.freeze({
  renderer: INITIAL.renderer,
  audioState: INITIAL.audioState,
  fps: INITIAL.fps,
  lastInputKind: INITIAL.lastInputKind,
  droppedFrames: INITIAL.droppedFrames,
});

export class RuntimeUIStore {
  private snapshot: RuntimeUISnapshot = INITIAL;
  private statusSnapshot: RuntimeUIStatusSnapshot = INITIAL_STATUS;
  private readonly listeners = new Set<() => void>();
  private revision = 0;
  private rendererName = 'initializing';
  private audioState = 'suspended';
  private error = '';
  private stageRanking: StageRankingResult | null = null;

  readonly getSnapshot = (): RuntimeUISnapshot => this.snapshot;
  readonly getServerSnapshot = (): RuntimeUISnapshot => INITIAL;
  readonly getModeSnapshot = (): GameMode => this.snapshot.mode;
  readonly getServerModeSnapshot = (): GameMode => INITIAL.mode;
  readonly getStatusSnapshot = (): RuntimeUIStatusSnapshot => this.statusSnapshot;
  readonly getServerStatusSnapshot = (): RuntimeUIStatusSnapshot => INITIAL_STATUS;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  setRendererName(value: string): void {
    this.rendererName = value;
  }

  setAudioState(value: string): void {
    this.audioState = value;
  }

  setError(value: string): void {
    this.error = value;
  }

  setStageRanking(value: StageRankingResult | null): void {
    this.stageRanking = value;
  }

  publish(world: GameWorld, input: InputManager, droppedFrames: number, fps: number): void {
    const stage = world.stageDirector.spec;
    const core = CORE_BEAMS[world.player.core] ?? CORE_BEAMS[0]!;
    const lens = NODE_LENSES[world.player.lens] ?? NODE_LENSES[0]!;
    const device = DEVICES[world.player.device] ?? DEVICES[0]!;
    const weaponLevel = world.player.weaponLevel;
    const gradeThresholds = world.continuousArsenalEnabled ? SYNC_GRADE_THRESHOLDS : POWER_THRESHOLDS;
    const powerNeeded = weaponLevel >= WEAPON_MAX_LEVEL ? 0 : gradeThresholds[weaponLevel];
    this.revision += 1;
    this.snapshot = Object.freeze({
      revision: this.revision,
      mode: world.mode,
      difficultyName: world.difficultySpec.name,
      stageId: world.currentStage,
      stageName: stage.name,
      stageSubtitle: stage.subtitle,
      stageProgress: Math.max(0, Math.min(1, world.stageDirector.stageTick / world.stageDirector.durationTicks)),
      score: world.player.score,
      lux: world.player.lux,
      continuousArsenal: world.continuousArsenalEnabled,
      syncTotal: world.continuousArsenalEnabled
        ? syncTotalForGrade(weaponLevel, world.player.powerCharge)
        : 0,
      syncTarget: SYNC_GRADE_TOTAL,
      lives: world.player.lives,
      maxLives: world.player.maxLives,
      chain: world.player.chain,
      chainTimerFraction: Math.max(0, Math.min(1, world.player.chainTimer / CHAIN_WINDOW_TICKS)),
      weaponLevel,
      powerCharge: world.player.powerCharge,
      powerNeeded,
      formation: FORMATION_NAMES[world.nodes.formation] ?? 'FRONT',
      nodeDirection: world.nodes.directionIndex,
      nodeCharge0: world.nodes.charge[0],
      nodeCharge1: world.nodes.charge[1],
      nodeCharge2: world.nodes.charge[2],
      coreName: core.name,
      coreGlyph: core.glyph,
      lensName: lens.name,
      lensGlyph: lens.glyph,
      deviceName: device.name,
      deviceGlyph: device.glyph,
      deviceState: world.deviceState,
      deviceCooldown: world.player.deviceCooldown,
      bossActive: world.bosses.active,
      bossName: world.bosses.active ? this.bossName(world.bosses.kind) : '',
      bossHp: world.bosses.hpRatio(),
      bossPhase: world.bosses.phaseName,
      musicLayer: world.musicLayer,
      stageBonus: world.stageBonus,
      stageRanking: this.stageRanking,
      gateSelection: world.gateSelection,
      gateMessage: world.gateMessageTicks > 0 ? (world.lastPurchaseSucceeded ? 'CALIBRATION APPLIED' : 'CALIBRATION REJECTED') : '',
      gateAheadTicks: nextGateTicks(world.stageDirector),
      gateApproachTicks: world.stageDirector.gateApproachTicks,
      continuesUsed: world.continuesUsed,
      lastInputKind: input.lastInputKind,
      droppedFrames,
      fps,
      renderer: this.rendererName,
      audioState: this.audioState,
      error: this.error,
    });
    const previousStatus = this.statusSnapshot;
    if (
      previousStatus.renderer !== this.rendererName
      || previousStatus.audioState !== this.audioState
      || previousStatus.fps !== fps
      || previousStatus.lastInputKind !== input.lastInputKind
      || previousStatus.droppedFrames !== droppedFrames
    ) {
      this.statusSnapshot = Object.freeze({
        renderer: this.rendererName,
        audioState: this.audioState,
        fps,
        lastInputKind: input.lastInputKind,
        droppedFrames,
      });
    }
    for (const listener of this.listeners) listener();
  }

  private bossName(kind: number): string {
    if (kind === 1) return 'CATALOGUE MOUTH';
    if (kind === 2) return 'NULL SCRIBE';
    if (kind === 3) return 'MNEMOSYNE ARRAY';
    return '';
  }
}
